import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
import type { Container } from '../types';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function formatCurrency(amount: number, currency: string = 'ISK'): string {
  return new Intl.NumberFormat('is-IS', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 0,
  }).format(amount);
}

export function formatDate(date: string | Date): string {
  return new Intl.DateTimeFormat('is-IS', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  }).format(new Date(date));
}

export function formatDateTime(date: string | Date): string {
  return new Intl.DateTimeFormat('is-IS', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(date));
}

export function formatPercent(value: number): string {
  return `${Math.round(value)}%`;
}

export function getStatusColor(status: string): string {
  const colors: Record<string, string> = {
    // Container statuses
    available: 'bg-green-100 text-green-800',
    rented: 'bg-blue-100 text-blue-800',
    maintenance: 'bg-yellow-100 text-yellow-800',
    transit: 'bg-purple-100 text-purple-800',
    // Customer statuses
    active: 'bg-green-100 text-green-800',
    inactive: 'bg-gray-100 text-gray-800',
    suspended: 'bg-red-100 text-red-800',
    // Order statuses
    pending: 'bg-yellow-100 text-yellow-800',
    confirmed: 'bg-blue-100 text-blue-800',
    in_progress: 'bg-purple-100 text-purple-800',
    completed: 'bg-green-100 text-green-800',
    cancelled: 'bg-red-100 text-red-800',
    // Invoice statuses
    draft: 'bg-gray-100 text-gray-800',
    sent: 'bg-blue-100 text-blue-800',
    paid: 'bg-green-100 text-green-800',
    overdue: 'bg-red-100 text-red-800',
    // Support ticket statuses
    open: 'bg-gray-100 text-gray-800',
    waiting_customer: 'bg-yellow-100 text-yellow-800',
    resolved: 'bg-green-100 text-green-800',
    closed: 'bg-gray-800 text-white',
  };
  return colors[status] || 'bg-gray-100 text-gray-800';
}

export function getPriorityColor(priority: string): string {
  const colors: Record<string, string> = {
    low: 'bg-gray-100 text-gray-800',
    medium: 'bg-yellow-100 text-yellow-800',
    high: 'bg-orange-100 text-orange-800',
    critical: 'bg-red-100 text-red-800',
  };
  return colors[priority] || 'bg-gray-100 text-gray-800';
}

export function getSeverityColor(severity: string): string {
  const colors: Record<string, string> = {
    low: 'bg-blue-100 text-blue-800',
    medium: 'bg-yellow-100 text-yellow-800',
    high: 'bg-orange-100 text-orange-800',
    critical: 'bg-red-100 text-red-800',
  };
  return colors[severity] || 'bg-gray-100 text-gray-800';
}

export function getFillLevelColor(level: number): string {
  if (level >= 90) return 'bg-red-500';
  if (level >= 75) return 'bg-orange-500';
  if (level >= 50) return 'bg-yellow-500';
  return 'bg-green-500';
}

const typeLabels: Record<string, string> = {
  open: 'Opinn',
  closed: 'Lokaður',
};

const statusLabels: Record<string, string> = {
  available: 'Laus',
  rented: 'Leigður',
  maintenance: 'Viðhald',
};

function str(value: string | number | null | undefined, fallback = ''): string {
  if (value === null || value === undefined) return fallback;
  return String(value);
}

function fmtDate(value: string | null | undefined, formatter: (v: string) => string): string {
  return value ? formatter(value) : '';
}

function containerToCSVRow(container: Container): string[] {
  return [
    str(container.id),
    typeLabels[container.type] || 'Efnisgámur',
    str(container.waste_type),
    statusLabels[container.status] || 'Á ferð',
    str(container.customer_id),
    str(container.location_address),
    str(container.location_lat),
    str(container.location_lon),
    str(container.fill_level, '0'),
    str(container.temperature),
    str(container.humidity),
    str(container.door_status),
    str(container.weight_kg),
    str(container.battery_level),
    fmtDate(container.last_reading, formatDateTime),
    fmtDate(container.rental_start_date, formatDate),
    fmtDate(container.rental_end_date, formatDate),
    container.ai_monitoring_enabled ? 'Já' : 'Nei',
    fmtDate(container.created_at, formatDateTime),
    fmtDate(container.updated_at, formatDateTime),
  ];
}

/**
 * Export containers data to CSV file
 * @param containers - Array of containers to export
 * @param filename - Optional filename (defaults to containers-YYYY-MM-DD.csv)
 */
export function exportContainersToCSV(containers: Container[], filename?: string): void {
  if (containers.length === 0) {
    alert('Engir gámar til að flytja út');
    return;
  }

  // Define CSV headers (Icelandic)
  const headers = [
    'Gámur ID',
    'Tegund',
    'Úrgangsgerð',
    'Staða',
    'Viðskiptavinur ID',
    'Staðsetning',
    'GPS Breiddargráða',
    'GPS Lengdargráða',
    'Fyllingarhlutfall (%)',
    'Hitastig (°C)',
    'Raki (%)',
    'Hurðastaða',
    'Þyngd (kg)',
    'Rafhlöðustaða (%)',
    'Síðasta mæling',
    'Leigutímabil byrjar',
    'Leigutímabil endar',
    'AI vöktun virk',
    'Búið til',
    'Uppfært',
  ];

  // Map containers to CSV rows
  const rows = containers.map(containerToCSVRow);

  // Escape CSV values (handle commas, quotes, newlines)
  const escapeCSV = (value: string): string => {
    if (value.includes(',') || value.includes('"') || value.includes('\n')) {
      return `"${value.replace(/"/g, '""')}"`;
    }
    return value;
  };

  // Build CSV content
  const csvContent = [
    headers.map(escapeCSV).join(','),
    ...rows.map(row => row.map(escapeCSV).join(','))
  ].join('\n');

  // Add BOM for proper Excel UTF-8 support
  const BOM = '\uFEFF';
  const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });

  // Generate filename with current date
  const defaultFilename = `gamar-${new Date().toISOString().split('T')[0]}.csv`;
  const finalFilename = filename || defaultFilename;

  // Trigger download
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', finalFilename);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
